import {
  FunctionComponent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import classnames from "classnames";

import { ActionLinkTypography, CounterColor, KeyboardSeparator, LinkItem } from "@ppb/the-wall-common/types";
import { SystemIconName } from "@ppb/the-wall-icons";
import { GenericIcon } from "@ppb/the-wall-icons/GenericIcon/GenericIcon";
import {
  ActionLink,
  CurrencyNumberInputField,
  Link,
  Option,
  PrimaryButton,
  QuickStakes,
  Styled,
  SilkWrapper,
} from "@ppb/the-wall-web";

import { Alert, AlertIcon } from "@ppb/the-wall-web/next/components/Alert";
import { Switch } from "@ppb/the-wall-web/next/components/Switch";

import { Minimized } from "../Minimized/Minimized.web";
import { ComponentProps } from "./props";
import { KeyboardContext } from "../../../Keyboard/KeyboardContext";
import { FooterCustomKeyboard } from "../../../Keyboard/FooterCustomKeyboard.web";
import { PromoButton } from "../../../PromoButton/PromoButton.web";
import styles from "./QuickBetslipView.module.css";
import { useScrollHandler } from "./useScrollHandler.web";
import { Notifier } from "../../../Notifier/Notifier.web";
import ConnectedNotifier from "../../../Notifier";
import { AnimatedDots } from "../../../AnimatedDots/AnimatedDots.web";

const titleStyle = {
  stake: styles.titleBoldPart,
  odds: styles.titleBoldPart,
  potentialReturns: styles.titleBoldPart,
};

const selectionDetailsStyle = {
  name: styles.selectionDetailsBoldPart,
};

const useQuickBetslipViewStates = (openBetslip: () => void, removeFocus: () => void) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [shouldRenderContent, setShouldRenderContent] = useState(true);

  const onScrollUp = useCallback(() => {
    if (!isExpanded) {
      setShouldRenderContent(true);
      setIsExpanded(true);
    }
  }, [isExpanded, setIsExpanded, setShouldRenderContent]);

  const onScrollDown = useCallback(() => {
    if (isExpanded) {
      setShouldRenderContent(true);
      setIsExpanded(false);
      removeFocus();
    }
  }, [isExpanded, setIsExpanded, setShouldRenderContent, removeFocus]);

  useScrollHandler(onScrollUp, onScrollDown);

  useEffect(() => {
    if (!isExpanded) {
      const timer = setTimeout(() => {
        setShouldRenderContent(false);
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isExpanded]);

  const openFullBetslip = useCallback(() => {
    removeFocus();
    openBetslip();
  }, [openBetslip, removeFocus]);

  // mousedown runs before input blur. A click handler loses the event: blur removes
  // the keyboard, layout shifts, and the click never lands on the header.
  const onTitleMouseDown = useCallback(
    (event: ReactMouseEvent) => {
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();
      openFullBetslip();
    },
    [openFullBetslip],
  );

  const onTitleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openFullBetslip();
      }
    },
    [openFullBetslip],
  );

  const expand = useCallback(() => setIsExpanded(true), []);

  return {
    isExpanded,
    expand,
    shouldRenderContent,
    onTitleMouseDown,
    onTitleKeyDown,
  };
};

export const QuickBetslipView: FunctionComponent<ComponentProps> = ({
  config,
  header,
  i18n,
  combinationId,
  combinationIds,
  placeButtonLabel,
  placeBtnLoadingLabel,
  hasCTALoading,
  currencySymbol,
  stake,
  isStakeValid,
  isLastStakeValid,
  quickStakes,
  termsUrl,
  isPlacing,
  hasPlaceError,
  hasGenerosity,
  isGenerosityWalletSelected = false,
  generosityAlertMessage,
  generosityType,
  generosityIconName,
  isPlaceDisabled,
  isDepositRequired,
  requiredDepositValue,
  lastSuccessfulStake,
  icon,
  silkFallbackType,
  hasEachWay,
  hasStartingPrice,
  isEachWaySelected,
  isStartingPriceSelected,
  eachWaySubtitle,
  multiplier,
  areBetOptionsDisabled = false,
  placeBtnSecondaryLabel,
  reverseLabels,

  // Accept Odds Movement
  showAcceptOddsMovementAlert,
  isOddsMovementOn,
  oddsMovementLabels,

  onClick: openBetslip,
  dispatchStakeChange,
  dispatchIncrementPress,
  dispatchPlacement,
  dispatchDepositRedirect,
  dispatchOddsMovementChange,
  dispatchEachWayPress,
  dispatchStartPricePress,
  dispatchGenerosityPress,
  dispatchGenerosityWalletRemoveAction,
  dispatchGenerosityRemoveAction,
}) => {
  const keyboardContext = useContext(KeyboardContext);
  const {
    focusedKeyboardControls: { focusedInputId },
    setFocusedKeyboardControls,
  } = keyboardContext;

  const inputRef = useRef(null);
  const hasStakeInit = useRef(false);
  const uniqueInputId = useId();

  const removeFocus = useCallback(() => {
    setFocusedKeyboardControls((prev) => ({
      ...prev,
      focusedInputId: null,
    }));
  }, [setFocusedKeyboardControls]);

  const { isExpanded, expand, shouldRenderContent, onTitleMouseDown, onTitleKeyDown } = useQuickBetslipViewStates(
    openBetslip,
    removeFocus,
  );
  const isFocused = isExpanded && uniqueInputId === focusedInputId;

  useEffect(() => {
    // Auto-populate on first open
    // On unmount the ref gets reset to false, restarting the cycle
    // Further adds produce no stake change dispatches
    if (!hasStakeInit.current && stake === undefined && lastSuccessfulStake !== undefined && isLastStakeValid) {
      dispatchStakeChange({ id: combinationId, newValue: lastSuccessfulStake });
      hasStakeInit.current = true;
    }
  }, [combinationId, dispatchStakeChange, isLastStakeValid, lastSuccessfulStake, stake]);

  const onStakeChangeGuard = useCallback(
    (newValue?: number) => {
      dispatchStakeChange({ id: combinationId, newValue });
    },
    [dispatchStakeChange, combinationId],
  );

  const onStakeFocusGuard = useCallback(() => {
    if (!isFocused) {
      dispatchStakeChange({ id: combinationId, newValue: undefined });

      expand();

      setFocusedKeyboardControls({
        focusedCombinationId: combinationId,
        focusedInputId: uniqueInputId,
        focusedInputRef: inputRef,
        focusedTargetRef: null,
      });
    }
  }, [dispatchStakeChange, combinationId, isFocused, expand, setFocusedKeyboardControls, uniqueInputId]);

  const onPlace = useCallback(() => {
    if (isDepositRequired) {
      dispatchDepositRedirect(requiredDepositValue);

      return;
    }
    dispatchPlacement();
  }, [dispatchDepositRedirect, dispatchPlacement, isDepositRequired, requiredDepositValue]);

  const handleOddsMovementChange = useCallback(
    (isChecked: boolean) => {
      dispatchOddsMovementChange(isChecked);
    },
    [dispatchOddsMovementChange],
  );

  const onQuickStakePress = useCallback(
    (stake: number) => {
      dispatchIncrementPress(combinationId, stake, currencySymbol);
    },
    [dispatchIncrementPress, combinationId, currencySymbol],
  );

  const onEachWayPress = useCallback(
    () => dispatchEachWayPress(combinationId, isEachWaySelected),
    [combinationId, dispatchEachWayPress, isEachWaySelected],
  );

  const onStartingPricePress = useCallback(
    () => dispatchStartPricePress(combinationId, isStartingPriceSelected),
    [combinationId, dispatchStartPricePress, isStartingPriceSelected],
  );

  const onGenerosityPress = useCallback(() => {
    dispatchGenerosityPress(combinationIds, !isGenerosityWalletSelected);
  }, [dispatchGenerosityPress, combinationIds, isGenerosityWalletSelected]);

  const onGenerosityRemovePress = useCallback(() => {
    dispatchGenerosityWalletRemoveAction(combinationIds, generosityType);
    dispatchGenerosityRemoveAction();
  }, [dispatchGenerosityWalletRemoveAction, combinationIds, generosityType, dispatchGenerosityRemoveAction]);

  const terms = useMemo(() => {
    if (!termsUrl) {
      return null;
    }

    const item: LinkItem = { viewLink: { viewUrl: termsUrl }, target: "_blank" };

    return (
      <div className={styles.termsContainer}>
        {i18n.termsLabel}{" "}
        <Link onClick={() => {}} item={item}>
          {i18n.termsLinkLabel}
        </Link>
      </div>
    );
  }, [termsUrl, i18n]);

  const footerPrefix = useMemo(() => {
    return (
      <div className={styles.quickStakesContainer}>
        <QuickStakes quickStakes={quickStakes} onTouch={onQuickStakePress} isDisabled={false} />
      </div>
    );
  }, [quickStakes, onQuickStakePress]);

  const isWrapperView = window.__TBD_CLIENT_CONTEXT__?.webWrappedExperience;
  const [shouldPlayEnterAnimation] = useState(true);

  if (!header) {
    return null;
  }

  return (
    <div
      className={classnames(styles.sheetWrapper, {
        [styles.sheetWrapperEnter]: shouldPlayEnterAnimation,
        [styles.webWrappedVersion]: isWrapperView,
      })}
    >
      <div className={styles.sheetContainer}>
        <button type="button" className={styles.sheetHeader} onMouseDown={onTitleMouseDown} onKeyDown={onTitleKeyDown}>
          <div className={styles.headerContainer}>
            <div className={styles.headerTitle}>
              <Minimized counter={header.counter} color={CounterColor.Teal}>
                <span className={styles.titleText}>
                  <Styled translation={header.title} styles={titleStyle} />
                </span>
              </Minimized>
            </div>
            <div className={styles.actionIcon}>
              <GenericIcon name={SystemIconName.CHEVRON_UP} color={"var(--expandable-icon-colour)"} />
            </div>
          </div>
        </button>

        <div
          className={classnames(styles.sheetContent, {
            [styles.sheetContentExpanded]: isExpanded || isFocused,
            [styles.sheetContentCollapsed]: !isExpanded && !isFocused,
          })}
        >
          <div className={classnames(styles.selectionDetails, { [styles.selectionDetailsCollapsed]: !isExpanded })}>
            <div className={styles.selectionDetailsInfo}>
              {icon && (
                <div className={styles.iconWrapper}>
                  <SilkWrapper silkUrl={icon} silkFallbackType={silkFallbackType} />
                </div>
              )}
              <div
                className={classnames(styles.selectionDetailsText, {
                  [styles.selectionDetailsInfoPriceBoostMultiple]: header.isPriceBoostMultiple,
                })}
              >
                <Styled translation={header.selectionDetails} styles={selectionDetailsStyle} />
              </div>
            </div>
            {header.moreLabel && <span className={styles.selectionDetailsMore}>{header.moreLabel}</span>}
          </div>
          {shouldRenderContent && (hasEachWay || hasStartingPrice) && (
            <div className={styles.betOptions}>
              {hasEachWay && (
                <div className={styles.eachWay}>
                  <Option
                    checkboxId={`ewquickbetcontrols:${combinationId}`}
                    title={i18n.eachWayTitle}
                    subtitle={eachWaySubtitle}
                    disabled={areBetOptionsDisabled}
                    isSelected={isEachWaySelected}
                    onPress={onEachWayPress}
                  />
                </div>
              )}
              {hasStartingPrice && (
                <div className={styles.startingPrice}>
                  <Option
                    checkboxId={`spquickbetcontrols:${combinationId}`}
                    title={i18n.startingPriceTitle}
                    disabled={areBetOptionsDisabled}
                    isSelected={isStartingPriceSelected}
                    onPress={onStartingPricePress}
                  />
                </div>
              )}
            </div>
          )}
          {shouldRenderContent && showAcceptOddsMovementAlert && (
            <div className={styles.oddsMovementAlertContainer}>
              <Alert
                title={oddsMovementLabels.message}
                description={oddsMovementLabels.detailMessage}
                variant="info"
                endContent={
                  <Switch
                    isChecked={isOddsMovementOn}
                    onChange={handleOddsMovementChange}
                    accessibilityLabel={oddsMovementLabels.message}
                  />
                }
              />
            </div>
          )}
          {shouldRenderContent && <ConnectedNotifier component={Notifier} />}
          {shouldRenderContent && !!generosityAlertMessage && (
            <div className={classnames({ [styles.generosityAlertContainer]: isFocused })}>
              <Alert
                title={generosityAlertMessage}
                variant="generosity"
                hasIcon
                icon={generosityIconName ? <AlertIcon icon={generosityIconName} /> : undefined}
                endContent={
                  areBetOptionsDisabled ? undefined : (
                    <ActionLink
                      text={i18n.generosityAlertRemoveLabel}
                      onClick={onGenerosityRemovePress}
                      typography={ActionLinkTypography.Regular}
                      noPadding
                      capitalize={false}
                    />
                  )
                }
              />
            </div>
          )}
          {shouldRenderContent && isFocused && (
            <div className={classnames(styles.focusedContent, { [styles.focusedContentVisible]: isFocused })}>
              <FooterCustomKeyboard
                prefix={footerPrefix}
                separator={KeyboardSeparator.Dot}
                isDisabled={false}
                shouldScrollIntoView={false}
                isDesktop={config.isDesktopLayout}
              />
              {terms}
            </div>
          )}
        </div>

        <div className={classnames(styles.sheetFooter, { [styles.sheetFooterCollapsed]: !isExpanded })}>
          {(shouldRenderContent || isPlacing) && (
            <>
              <div className={styles.stakeInput}>
                <CurrencyNumberInputField
                  ref={inputRef}
                  id={combinationId}
                  disabled={isPlacing}
                  readonly={false}
                  label={i18n.stakePlaceholder}
                  value={stake}
                  invalid={!isStakeValid}
                  separator={KeyboardSeparator.Dot}
                  focused={isFocused}
                  hasCaret
                  currencySymbol={currencySymbol}
                  multiplier={multiplier}
                  onChange={onStakeChangeGuard}
                  onFocus={onStakeFocusGuard}
                />
              </div>
              <div className={styles.placeButton}>
                <PrimaryButton
                  disabled={isPlacing || isPlaceDisabled}
                  label={placeButtonLabel}
                  loadingLabel={hasCTALoading ? placeBtnLoadingLabel : undefined}
                  secondaryLabel={!Array.isArray(placeBtnSecondaryLabel) ? placeBtnSecondaryLabel : undefined}
                  stopAnimation={hasPlaceError || isDepositRequired}
                  loadingChildren={<AnimatedDots />}
                  onTap={onPlace}
                  variant="transactional"
                  reverseLabels={reverseLabels}
                />
              </div>
              {hasGenerosity && (
                <PromoButton
                  isSelected={isGenerosityWalletSelected}
                  disabled={areBetOptionsDisabled}
                  onPress={onGenerosityPress}
                  promoIconName={generosityIconName}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
